import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faSearch,
  faEye,
  faPlus,
  faRotate,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { platformCompanyService } from '@/services/platform-company.service';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { canPlatform } from '@/lib/platform-permissions';
import type {
  PlatformPlan,
  PlatformTenantListItem,
  TenantStatus,
} from '@/types/platform-auth.types';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import { CompanyStatusBadge, formatCompanyDate } from './company-presentation';

/**
 * Phase 12.3G — Companies List.
 *
 * Design of record: `COMPANIES_DESIGN.md` v1.0 §5.1. Six columns, server-side
 * search / status / plan, fixed newest-first ordering, server-side pagination
 * at 20/50/100, an explicit Refresh, and `View` as the only row action.
 *
 * ── What changed from the 12.3E placeholder ────────────────────────────────
 * The previous page fetched ONE page and then filtered it in the browser, so
 * searching "acme" found nothing unless Acme happened to be in the first
 * twenty rows — a search box that silently lied. Search, status and plan are
 * now all parameters to the API, and the row count reflects the whole filtered
 * set rather than the current page.
 */

const PAGE_SIZES = [20, 50, 100] as const;

const STATUS_FILTERS: { value: '' | TenantStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'TRIAL', label: 'Trial' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/** §5.1 loading state — a skeleton table, not a spinner that hides the shape. */
function SkeletonRows({ rows = 6 }: { rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <tr key={i} className="animate-pulse">
          {Array.from({ length: 6 }).map((__, j) => (
            <td key={j} className="px-4 py-3.5">
              <div className="h-3 rounded bg-muted" style={{ width: j === 0 ? '70%' : '45%' }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function CompaniesPage() {
  const navigate = useNavigate();
  const role = usePlatformAuthStore((s) => s.user?.role);
  const canCreate = canPlatform(role, 'platform:companies:create');
  // The plan list lives behind `platform:subscriptions:view`, which
  // `platform:companies:view` does NOT imply — platform-support holds one and
  // not the other. Rather than show a filter that 403s, it is simply absent for
  // those roles. The backend remains the authority either way.
  const canSeePlans = canPlatform(role, 'platform:subscriptions:view');

  const [companies, setCompanies] = useState<PlatformTenantListItem[]>([]);
  const [plans, setPlans] = useState<PlatformPlan[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | TenantStatus>('');
  const [planId, setPlanId] = useState('');
  const [limit, setLimit] = useState<number>(PAGE_SIZES[0]);
  const [offset, setOffset] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  // Debounced search: one request per pause in typing, not one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setOffset(0); // a new search always starts at page 1
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Guards against a slow earlier request overwriting a newer one's results.
  const requestId = useRef(0);

  useEffect(() => {
    const current = ++requestId.current;
    setLoading(true);
    setError('');

    platformCompanyService
      .list({
        limit,
        offset,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...(planId ? { planId } : {}),
      })
      .then((page) => {
        if (current !== requestId.current) return;
        setCompanies(page.items);
        setTotal(page.total);
      })
      .catch(() => {
        if (current !== requestId.current) return;
        // 12.2B honesty rule: an error is never rendered as an empty table.
        setCompanies([]);
        setError('Could not load companies. Check your connection and try again.');
      })
      .finally(() => {
        if (current === requestId.current) setLoading(false);
      });
  }, [limit, offset, search, status, planId, reloadToken]);

  useEffect(() => {
    if (!canSeePlans) return;
    platformCompanyService
      .listPlans()
      .then((all) => setPlans(all.filter((p) => p.isActive)))
      // A missing plan list costs the Plan filter, nothing else. The page must
      // not fail because an optional filter could not be populated.
      .catch(() => setPlans([]));
  }, [canSeePlans]);

  const hasFilters = search !== '' || status !== '' || planId !== '';
  const clearFilters = useCallback(() => {
    setSearchInput('');
    setSearch('');
    setStatus('');
    setPlanId('');
    setOffset(0);
  }, []);

  const openCompany = useCallback(
    (tenantId: string) => {
      // Guard retained from 12.2A: interpolating an absent id yields the literal
      // "undefined" in the URL, which routes but then fails at the API.
      if (!tenantId) return;
      navigate(PLATFORM_ROUTES.COMPANY_DETAIL.replace(':id', tenantId));
    },
    [navigate],
  );

  const rangeLabel = useMemo(() => {
    if (total === 0) return 'No companies';
    const first = offset + 1;
    const last = Math.min(offset + companies.length, total);
    return `Showing ${first}–${last} of ${total}`;
  }, [offset, companies.length, total]);

  const showEmpty = !loading && !error && companies.length === 0 && !hasFilters;
  const showNoResults = !loading && !error && companies.length === 0 && hasFilters;

  return (
    <div className="space-y-5">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-foreground">Companies</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Manage tenant companies on this platform.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setReloadToken((t) => t + 1)}
            disabled={loading}
            aria-label="Refresh companies"
            className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50 sm:min-h-0"
          >
            <FontAwesomeIcon icon={faRotate} className="h-3.5 w-3.5" spin={loading} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          {/* §5.1 — hidden, not disabled, for roles without the permission. */}
          {canCreate && (
            <button
              type="button"
              onClick={() => navigate(PLATFORM_ROUTES.COMPANY_CREATE)}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:min-h-0"
            >
              <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
              New Company
            </button>
          )}
        </div>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_auto_auto]">
        <div className="relative min-w-0">
          <FontAwesomeIcon
            icon={faSearch}
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            aria-label="Search companies by name or slug"
            placeholder="Search by name or slug…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="h-11 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
          />
        </div>

        <select
          aria-label="Filter by company status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as '' | TenantStatus);
            setOffset(0);
          }}
          className="h-11 min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>

        {canSeePlans && (
          <select
            aria-label="Filter by subscription plan"
            value={planId}
            onChange={(e) => {
              setPlanId(e.target.value);
              setOffset(0);
            }}
            className="h-11 min-w-0 rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-9"
          >
            <option value="">All plans</option>
            {plans.map((plan) => (
              <option key={plan.planId} value={plan.planId}>
                {plan.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {error && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3"
        >
          <FontAwesomeIcon icon={faTriangleExclamation} className="h-4 w-4 text-destructive" />
          <p className="min-w-0 flex-1 text-sm text-destructive">{error}</p>
          <button
            type="button"
            onClick={() => setReloadToken((t) => t + 1)}
            className="rounded-lg border border-destructive/40 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            Try again
          </button>
        </div>
      )}

      {/* ── Desktop table ──────────────────────────────────────────────────── */}
      <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <caption className="sr-only">
              Companies on this platform, newest first. {rangeLabel}.
            </caption>
            <thead className="bg-muted/30">
              <tr>
                {['Company', 'Status', 'Plan', 'Subscription', 'Created'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {heading}
                  </th>
                ))}
                <th
                  scope="col"
                  className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <SkeletonRows />
              ) : showEmpty || showNoResults || error ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <FontAwesomeIcon icon={faBuilding} className="mb-2 h-6 w-6 opacity-30" />
                    <p className="text-sm text-muted-foreground">
                      {error
                        ? 'Companies could not be loaded.'
                        : showEmpty
                          ? 'No companies yet.'
                          : 'No companies match your search or filters.'}
                    </p>
                    {showNoResults && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="mt-3 text-sm font-medium text-primary hover:underline"
                      >
                        Clear filters
                      </button>
                    )}
                    {showEmpty && canCreate && (
                      <button
                        type="button"
                        onClick={() => navigate(PLATFORM_ROUTES.COMPANY_CREATE)}
                        className="mt-3 text-sm font-medium text-primary hover:underline"
                      >
                        Create the first company
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                companies.map((company) => (
                  <tr key={company.tenantId} className="transition-colors hover:bg-muted/20">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{company.name}</p>
                      <p className="text-xs text-muted-foreground">{company.slug}</p>
                    </td>
                    <td className="px-4 py-3">
                      <CompanyStatusBadge status={company.status} />
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{company.planName ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {company.subscriptionStatus ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                      {formatCompanyDate(company.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        disabled={!company.tenantId}
                        onClick={() => openCompany(company.tenantId)}
                        aria-label={`View ${company.name}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Narrow cards (§5.4) ────────────────────────────────────────────── */}
      {/* A six-column table cannot survive 375px, so below md the same rows are
          a semantic list of cards. Labels accompany values once headers are gone. */}
      <ul className="grid list-none grid-cols-1 gap-3 md:hidden">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <li key={i} className="animate-pulse rounded-xl border border-border bg-card p-4">
              <div className="h-3 w-2/3 rounded bg-muted" />
              <div className="mt-3 h-3 w-1/3 rounded bg-muted" />
            </li>
          ))
        ) : showEmpty || showNoResults || error ? (
          <li className="rounded-xl border border-border bg-card px-4 py-10 text-center">
            <FontAwesomeIcon icon={faBuilding} className="mb-2 h-6 w-6 opacity-30" />
            <p className="text-sm text-muted-foreground">
              {error
                ? 'Companies could not be loaded.'
                : showEmpty
                  ? 'No companies yet.'
                  : 'No companies match your search or filters.'}
            </p>
            {showNoResults && (
              <button
                type="button"
                onClick={clearFilters}
                className="mt-3 text-sm font-medium text-primary hover:underline"
              >
                Clear filters
              </button>
            )}
          </li>
        ) : (
          companies.map((company) => (
            <li key={company.tenantId}>
              <button
                type="button"
                onClick={() => openCompany(company.tenantId)}
                className="w-full rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-muted/20"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{company.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{company.slug}</p>
                  </div>
                  <CompanyStatusBadge status={company.status} />
                </div>
                <dl className="mt-3 grid grid-cols-1 gap-1.5 text-xs">
                  <div className="flex gap-1.5">
                    <dt className="text-muted-foreground">Plan:</dt>
                    <dd className="min-w-0 truncate text-foreground">{company.planName ?? '—'}</dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="text-muted-foreground">Subscription:</dt>
                    <dd className="min-w-0 truncate text-foreground">
                      {company.subscriptionStatus ?? '—'}
                    </dd>
                  </div>
                  <div className="flex gap-1.5">
                    <dt className="text-muted-foreground">Created:</dt>
                    <dd className="text-foreground">{formatCompanyDate(company.createdAt)}</dd>
                  </div>
                </dl>
              </button>
            </li>
          ))
        )}
      </ul>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      <nav
        aria-label="Companies pagination"
        className="flex flex-wrap items-center justify-between gap-3"
      >
        <p className="text-xs text-muted-foreground">{loading ? 'Loading…' : rangeLabel}</p>

        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Per page</span>
            <select
              aria-label="Companies per page"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setOffset(0);
              }}
              className="h-9 rounded-lg border border-border bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => setOffset((o) => Math.max(0, o - limit))}
            disabled={loading || offset === 0}
            className="min-h-11 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setOffset((o) => o + limit)}
            disabled={loading || offset + companies.length >= total}
            className="min-h-11 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 sm:min-h-0"
          >
            Next
          </button>
        </div>
      </nav>
    </div>
  );
}
